import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useData, save } from "../hooks/useData";
import { api } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { PageHeader, DataTable, Button, Modal, Field, Input, Textarea, Select, StatusBadge, SizeEditor } from "../components/shared";
import { Plus, Trash2 } from "lucide-react";
import { num, fmtDate } from "../lib/api";

const ITEM_TYPES = ["Full Pant", "Half Pant", "Boys", "Girls", "Shirt", "T-Shirt", "Other"];
const OPTIONAL = ["Embroidery", "Printing", "Emboss", "Bartec", "Washing"];

export default function ProductionOrders() {
  const { data, loading, reload } = useData("/production-orders");
  const { data: procs } = useData("/processes");
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "Admin";
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(null);
  const [sendOrder, setSendOrder] = useState(null);
  const [cuttingMaster, setCuttingMaster] = useState("");

  const blank = { sales_ref: "", design_number: "", item_type: "Full Pant", gender: "Boys", colour: "", sizes: [{ size: "", qty: 0 }], cutting_master: "", cutting_ratio: "", start_date: new Date().toISOString().slice(0, 10), target_date: "", selected_optional: [], remarks: "" };
  const openForm = (r) => { setForm(r ? { ...r, sizes: (r.sizes && r.sizes.length) ? r.sizes : [{ size: "", qty: 0 }], selected_optional: r.selected_optional || [] } : blank); setOpen(true); };
  const toggleOpt = (p) => setForm((f) => ({ ...f, selected_optional: f.selected_optional.includes(p) ? f.selected_optional.filter((x) => x !== p) : [...f.selected_optional, p] }));

  const submit = async (e) => {
    e.preventDefault();
    const clean = { ...form, sizes: form.sizes.filter((s) => s.size && s.qty) };
    await save(form.id ? "put" : "post", form.id ? `/production-orders/${form.id}` : "/production-orders", clean);
    toast.success(form.id ? "Production order updated" : "Production order created"); setOpen(false); reload();
  };

  const delOrder = async (r) => {
    if (!isAdmin) return;
    if (!window.confirm(`This will permanently delete Design / Production Order ${r.code} (${r.design_number}). This action may affect related production records. Do you want to continue?`)) return;
    const reason = window.prompt("Reason for deletion (recorded in the audit log):", "");
    if (reason === null) return;
    try { await api.delete(`/production-orders/${r.id}`, { params: { reason } }); toast.success("Design deleted"); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };


  const started = (o) => (o.stages || []).some((s) => s.name === "Cutting" && (s.qty_in || 0) > 0);
  const pending = (data || []).filter((o) => !started(o) && !o.sent_for_cutting);

  const openSend = (o) => { setSendOrder(o); setCuttingMaster(o.cutting_master || ""); };
  const confirmSend = async () => {
    if (!cuttingMaster.trim()) { toast.error("Enter cutting master name"); return; }
    await save("post", `/production-orders/${sendOrder.id}/send-for-cutting`, { cutting_master: cuttingMaster });
    toast.success(`${sendOrder.code} sent for cutting`); setSendOrder(null); reload();
  };

  const columns = [
    { key: "code", header: "Prod Order", mono: true, render: (r) => <span className="font-semibold text-blue-800">{r.code}</span> },
    { key: "design_number", header: "Design", render: (r) => <span className="font-medium">{r.design_number}</span> },
    { key: "item_type", header: "Item" },
    { key: "colour", header: "Colour" },
    { key: "total_qty", header: "Order Qty", align: "right", render: (r) => num(r.total_qty) },
    { key: "route", header: "Processes", sortable: false, render: (r) => <span className="text-xs text-slate-500">{(r.route || []).length} stages</span> },
    { key: "target_date", header: "Target", render: (r) => fmtDate(r.target_date) },
    { key: "act", header: "Actions", sortable: false, render: (r) => (
      <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
        <Button size="sm" variant="secondary" onClick={() => navigate(`/production-orders/${r.id}`)} data-testid={`po-view-${r.id}`}>View</Button>
        <Button size="sm" onClick={() => openSend(r)} data-testid={`po-send-cut-${r.id}`}>Send for Cutting</Button>
        <button title="Edit" onClick={() => openForm(r)} className="px-2 py-1 text-xs text-slate-600 hover:text-slate-900" data-testid={`prod-edit-${r.id}`}>Edit</button>
        {isAdmin && <button title="Delete" onClick={() => delOrder(r)} className="p-1.5 text-slate-400 hover:text-red-600" data-testid={`prod-delete-${r.id}`}><Trash2 size={15} /></button>}
      </div>
    ) },
  ];

  return (
    <div>
      <PageHeader title="Production Orders" subtitle="New orders awaiting cutting. Once cut, they move to Production Stage.">
        <Button onClick={() => openForm(null)} data-testid="add-prod-btn"><Plus size={15} /> New Production Order</Button>
      </PageHeader>
      {!loading && <DataTable testid="prod-table" columns={columns} rows={pending} exportName="production-orders.csv" onRowClick={(r) => navigate(`/production-orders/${r.id}`)} />}

      <Modal open={!!sendOrder} onClose={() => setSendOrder(null)} title="Send for Cutting">
        {sendOrder && (
          <div className="space-y-4">
            <div className="bg-slate-50 rounded-md p-3 text-sm">
              <b>{sendOrder.code}</b> · {sendOrder.design_number} · {sendOrder.colour} · {num(sendOrder.total_qty)} pcs
            </div>
            <Field label="Cutting Master" required>
              <Input value={cuttingMaster} onChange={(e) => setCuttingMaster(e.target.value)} placeholder="Enter cutting master name" required data-testid="send-cut-master" />
            </Field>
            <p className="text-sm text-slate-600">Are you sure you want to send this order for cutting? It will move to the Cutting tab and no longer appear here.</p>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setSendOrder(null)}>Cancel</Button>
              <Button onClick={confirmSend} data-testid="send-cut-confirm">Yes, Send for Cutting</Button>
            </div>
          </div>
        )}
      </Modal>

      <Modal open={open} onClose={() => setOpen(false)} title={form?.id ? "Edit Production Order" : "New Production Order"} wide>
        {form && (
          <form onSubmit={submit} className="grid grid-cols-3 gap-4">
            <Field label="Design Number" required><Input value={form.design_number} onChange={(e) => setForm({ ...form, design_number: e.target.value })} required data-testid="prod-design" /></Field>
            <Field label="Item Type"><Select value={form.item_type} onChange={(e) => setForm({ ...form, item_type: e.target.value })} options={ITEM_TYPES} /></Field>
            <Field label="Gender"><Select value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value })} options={["Boys", "Girls", "Men", "Women", "Unisex"]} /></Field>
            <Field label="Colour" required><Input value={form.colour} onChange={(e) => setForm({ ...form, colour: e.target.value })} required data-testid="prod-colour" /></Field>
            <Field label="Sales / Customer Ref"><Input value={form.sales_ref} onChange={(e) => setForm({ ...form, sales_ref: e.target.value })} /></Field>
            <Field label="Cutting Master"><Input value={form.cutting_master} onChange={(e) => setForm({ ...form, cutting_master: e.target.value })} /></Field>
            <Field label="Cutting Ratio"><Input value={form.cutting_ratio} onChange={(e) => setForm({ ...form, cutting_ratio: e.target.value })} placeholder="1:2:3:2" /></Field>
            <Field label="Start Date"><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></Field>
            <Field label="Target Date"><Input type="date" value={form.target_date} onChange={(e) => setForm({ ...form, target_date: e.target.value })} /></Field>
            <Field label="Size-wise Quantity" className="col-span-3">
              <SizeEditor testid="prod-sizes" sizes={form.sizes} onChange={(s) => setForm({ ...form, sizes: s })} />
            </Field>
            <Field label="Optional Processes (Cutting → Stitching → Thread Cutting → Checking → Finishing are always included)" className="col-span-3">
              <div className="flex flex-wrap gap-2">
                {((procs && procs.length) ? procs.filter((pr) => !pr.mandatory && pr.active !== false).map((pr) => pr.name) : OPTIONAL).map((p) => (
                  <button type="button" key={p} onClick={() => toggleOpt(p)} data-testid={`opt-${p.toLowerCase()}`}
                    className={`px-2.5 py-1 rounded-full text-xs border ${form.selected_optional.includes(p) ? "bg-blue-800 text-white border-blue-800" : "bg-white text-slate-600 border-slate-300"}`}>
                    {p}
                  </button>
                ))}
              </div>
            </Field>
            <Field label="Remarks" className="col-span-3"><Textarea value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} /></Field>
            <div className="col-span-3 flex justify-end gap-2 pt-2">
              <Button type="button" variant="secondary" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="prod-save">{form.id ? "Save Changes" : "Create Order"}</Button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
}
